/**
 * Fatigue Calculator Utility
 * Calculates creative usage fatigue levels to prevent overuse
 */

/**
 * Calculate days since a given date
 * @param {Date|string} date - The date to compare
 * @returns {number} - Days since the date (0 if today, negative if future)
 */
export const getDaysSinceDate = (date) => {
    if (!date) return Infinity;
    const targetDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);
    const diffTime = today - targetDate;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculate fatigue level based on usage count and recency
 * @param {number} usageCount - Number of times the creative has been used
 * @param {Date|string|null} lastUsedDate - When the creative was last used
 * @returns {'fresh'|'low'|'medium'|'high'} - Fatigue level
 */
export const calculateFatigueLevel = (usageCount, lastUsedDate) => {
    if (usageCount === 0) return 'fresh';

    const daysSinceUse = getDaysSinceDate(lastUsedDate);

    // High fatigue: 4+ uses regardless of recency
    if (usageCount >= 4) return 'high';

    // Medium fatigue: 2-3 uses
    if (usageCount >= 2) {
        // If used recently (within 7 days), slightly higher concern
        if (daysSinceUse < 7) return 'medium';
        return 'medium';
    }

    // Low fatigue: 1 use
    return 'low';
};

/**
 * Get CSS color variable for fatigue level
 * @param {'fresh'|'low'|'medium'|'high'} level - Fatigue level
 * @returns {string} - CSS variable name
 */
export const getFatigueColor = (level) => {
    switch (level) {
        case 'fresh':
            return 'var(--color-success)';
        case 'low':
            return 'var(--color-success)';
        case 'medium':
            return 'var(--color-warning)';
        case 'high':
            return 'var(--color-error)';
        default:
            return 'var(--color-text-secondary)';
    }
};

/**
 * Get display label for fatigue level
 * @param {'fresh'|'low'|'medium'|'high'} level - Fatigue level
 * @returns {string} - Human-readable label
 */
export const getFatigueLabel = (level) => {
    switch (level) {
        case 'fresh':
            return 'New';
        case 'low':
            return 'OK';
        case 'medium':
            return 'Moderate';
        case 'high':
            return 'HIGH';
        default:
            return '';
    }
};

/**
 * Format last used date for display
 * @param {Date|string|null} lastUsedDate - When the creative was last used
 * @returns {string} - Formatted string like "3 days ago" or "2 weeks ago"
 */
export const formatLastUsed = (lastUsedDate) => {
    if (!lastUsedDate) return 'Never used';

    const days = getDaysSinceDate(lastUsedDate);

    if (days < 0) return 'Scheduled';
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 14) return '1 week ago';
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 60) return '1 month ago';
    return `${Math.floor(days / 30)} months ago`;
};

/**
 * Calculate usage stats for a creative based on scheduled posts
 * @param {string} creativeId - The creative ID
 * @param {Array} scheduledPosts - Array of all scheduled posts
 * @returns {{usageCount: number, lastUsedDate: Date|null, fatigueLevel: string}}
 */
export const getCreativeUsageStats = (creativeId, scheduledPosts) => {
    const postsWithCreative = scheduledPosts.filter(
        post => post.creativeId === creativeId
    );

    const usageCount = postsWithCreative.length;

    // Find the most recent scheduled date
    let lastUsedDate = null;
    if (postsWithCreative.length > 0) {
        const dates = postsWithCreative
            .map(p => new Date(p.scheduledDate))
            .filter(d => !isNaN(d.getTime()))
            .sort((a, b) => b - a);

        if (dates.length > 0) {
            lastUsedDate = dates[0];
        }
    }

    const fatigueLevel = calculateFatigueLevel(usageCount, lastUsedDate);

    return {
        usageCount,
        lastUsedDate,
        fatigueLevel
    };
};

/**
 * Sort creatives by fatigue (freshest first)
 * @param {Array} creatives - Array of creatives with usage stats
 * @returns {Array} - Sorted array
 */
export const sortByFreshness = (creatives) => {
    const levelOrder = { fresh: 0, low: 1, medium: 2, high: 3 };

    return [...creatives].sort((a, b) => {
        const aLevel = levelOrder[a.fatigueLevel] ?? 4;
        const bLevel = levelOrder[b.fatigueLevel] ?? 4;

        if (aLevel !== bLevel) return aLevel - bLevel;

        // If same level, sort by days since last use (longer ago = better)
        const aDays = getDaysSinceDate(a.lastUsedDate);
        const bDays = getDaysSinceDate(b.lastUsedDate);
        return bDays - aDays;
    });
};
